import {
  dateMax,
  dateAdd,
  dateDiff,
  dateFormat,
  dateIsAfter,
} from 'javascripts/util/dateFns';

export const PRESET_DATES = {
  THREE_MONTHS: 91,
  SIX_MONTHS: 182,
  ONE_YEAR: 365,
};

export const ACTIVE_BUTTON_STATE = {
  CUSTOM: 'custom',
  FIT: 'fit',
  THREE_MONTHS: 'threeMonths',
  SIX_MONTHS: 'sixMonths',
  ONE_YEAR: 'oneYear',
};

/**
 * Derive the Active Button state based on the state passed in. If none are found, return custom.
 *
 * @param {Date|String} visibleStartDay Visible start date string in preset
 * @param {Date|String} visibleEndDay Visible End Date String in preset
 * @param {Date|String} minStartDate Min Date String for fill
 * @param {Date|String} maxStartDate Max date string for fill
 * @param {String|null} zoom Current zoom type
 */
export function deriveInitialPresetState(
  visibleStartDay,
  visibleEndDay,
  minStartDate,
  maxStartDate,
  zoom = null
) {
  // If the zoom was set to 'fit', keep it as 'fit'
  if (zoom === 'fit') {
    return ACTIVE_BUTTON_STATE.FIT;
  }

  if (
    dateDiff(visibleStartDay, visibleEndDay) ===
    dateDiff(minStartDate, maxStartDate)
  ) {
    return ACTIVE_BUTTON_STATE.FIT;
  }

  const range = dateDiff(visibleStartDay, visibleEndDay);
  const presetKeys = Object.keys(PRESET_DATES);

  for (let i = 0; i < presetKeys.length; i++) {
    if (PRESET_DATES[presetKeys[i]] === range) {
      return ACTIVE_BUTTON_STATE[presetKeys[i]];
    }
  }

  return ACTIVE_BUTTON_STATE.CUSTOM;
}

/**
 * Zoom element to a given preset range, defined in the PRESET_DATES above
 *
 * @param {number} numberOfDays Preset desired
 * @param {Object} options Additional date information needed to set date
 */
export function zoomToPresetRange(
  numberOfDays,
  {
    hideWeekends,
    onSetEndDate,
    onSetVisibleDatesHandler,
    minStartDate,
    maxEndDate,
  }
) {
  // Calculate a new visible end date, with a min of 7 days.
  const newVisibleEndDate = dateMax(
    dateAdd(minStartDate, numberOfDays, { business: hideWeekends }),
    dateAdd(minStartDate, 7, {
      business: hideWeekends,
    })
  );

  if (dateIsAfter(newVisibleEndDate, maxEndDate)) {
    onSetEndDate(newVisibleEndDate);
  }

  onSetVisibleDatesHandler(minStartDate, newVisibleEndDate);
}

/**
 * Zoom to a custom selected date range
 *
 * @param {string} customStartDate New start date for visible area
 * @param {string} customEndDate New end date for the visible area
 * @param {Object} options Additional date information to set date
 */
export function zoomToCustomRange(customStartDate, customEndDate, options) {
  const {
    hideWeekends,
    onSetEndDate,
    onSetStartDate,
    onSetVisibleDatesHandler,
    onSetZoom,
  } = options;

  // Don't do anything if we're being passed an invalid date. For some reason this seems to happen pretty frequently, likely due to one callback triggering another callback and so on.
  if ([customStartDate, customEndDate].some(m => m === 'Invalid date')) return;

  const formattedCustomStartDate = dateFormat(customStartDate);
  const formattedCustomEndDate = dateFormat(customEndDate);

  // Get new visible end date, ensuring there is at least 7 days visible to the gantt chart
  const newVisibleEndDate = dateMax(
    formattedCustomEndDate,
    dateAdd(formattedCustomStartDate, 7, {
      business: hideWeekends,
    })
  );

  onSetZoom('custom');
  onSetStartDate(formattedCustomStartDate);
  onSetEndDate(newVisibleEndDate);

  // Set new visible dates
  onSetVisibleDatesHandler(formattedCustomStartDate, newVisibleEndDate);
}

/**
 * Check if any class in a list exists within a given dom classlast. If any exist, return
 * true, else return false.
 *
 * @param {array} classes Array of class name strings to check for
 * @param {classlist} classList DOM element classlist
 */
export function classExists(classes, classList) {
  for (let i = 0; i < classes.length; i++) {
    if (classList.contains(classes[i])) {
      return true;
    }
  }
  return false;
}
